import {
  type FormEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { PageMeta } from "../components/PageMeta";
import { useI18n } from "../i18n";
import {
  type AdminUser,
  type AdminView,
  adminInviteEmail,
  api,
  emptyMetrics,
  emptyPagination,
  formatBytes,
  formatRecipients,
  type InboundDetail,
  type InboundRecord,
  type InquiryRecord,
  type ListResponse,
  listUrl,
  type Metrics,
  type OutboundDetail,
  type OutboundRecord,
  type Pagination,
  type RecordStatus,
  recordsForView,
  type ViewRecords,
} from "./admin-model";

type Session = {
  user: { id: string; name: string; email: string; role?: string };
};
type Screen = "checking" | "setup" | "login" | "ready";
type RecordItem = InquiryRecord | InboundRecord | OutboundRecord | AdminUser;
type ComposeState = {
  mode: "new" | "reply" | "forward";
  source?: InboundDetail;
};

export function AdminPage() {
  const { l, locale } = useI18n();
  const [invite] = useState(() => {
    if (typeof window === "undefined") return "";
    return (
      new URLSearchParams(window.location.hash.slice(1)).get("invite") ?? ""
    );
  });
  const invitedEmail = adminInviteEmail(invite);
  const [screen, setScreen] = useState<Screen>("checking");
  const [session, setSession] = useState<Session | null>(null);
  const [view, setView] = useState<AdminView>("inquiries");
  const [metrics, setMetrics] = useState<Metrics>(emptyMetrics);
  const [recordSet, setRecordSet] = useState<ViewRecords<RecordItem>>({
    view: "inquiries",
    records: [],
  });
  const records = recordsForView(view, recordSet);
  const [pagination, setPagination] = useState<Pagination>(emptyPagination);
  const [page, setPage] = useState(1);
  const [queryInput, setQueryInput] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [inboundDetail, setInboundDetail] = useState<InboundDetail | null>(
    null,
  );
  const [outboundDetail, setOutboundDetail] = useState<OutboundDetail | null>(
    null,
  );
  const [compose, setCompose] = useState<ComposeState | null>(null);
  const [userDialog, setUserDialog] = useState<
    { mode: "create" } | { mode: "password"; user: AdminUser } | null
  >(null);
  const [confirmation, setConfirmation] = useState<{
    title: string;
    message: string;
    action: () => Promise<void>;
  } | null>(null);

  const initialize = useCallback(async () => {
    try {
      const [setup, authSession] = await Promise.all([
        api<{ needsSetup: boolean }>("/api/admin/setup-status"),
        fetch("/api/auth/get-session", { credentials: "include" }).then(
          async (response) =>
            response.ok ? ((await response.json()) as Session | null) : null,
        ),
      ]);
      if (setup.needsSetup) {
        setScreen("setup");
      } else if (authSession?.user) {
        setSession(authSession);
        setScreen("ready");
      } else {
        setScreen("login");
      }
    } catch {
      setScreen("login");
    }
  }, []);

  useEffect(() => {
    void initialize();
  }, [initialize]);

  const loadMetrics = useCallback(async () => {
    setMetrics(await api<Metrics>("/api/admin/overview"));
  }, []);

  const loadRecords = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      setError("");
      try {
        if (view === "users") {
          const response = await api<{ records: AdminUser[] }>(
            "/api/admin/users",
            { signal },
          );
          if (signal?.aborted) return;
          setRecordSet({ view, records: response.records });
          setPagination({
            page: 1,
            pageSize: response.records.length,
            total: response.records.length,
            pages: 1,
          });
        } else {
          const response = await api<ListResponse<RecordItem>>(
            listUrl(view, page, query, status),
            { signal },
          );
          if (signal?.aborted) return;
          setRecordSet({ view, records: response.records });
          setPagination(response.pagination);
        }
      } catch (caught) {
        if (signal?.aborted) return;
        if (
          caught instanceof Error &&
          /Unauthorized|Administrator/.test(caught.message)
        ) {
          setScreen("login");
          setSession(null);
        } else {
          setError(
            caught instanceof Error ? caught.message : "Could not load records",
          );
        }
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [page, query, status, view],
  );

  useEffect(() => {
    if (screen !== "ready") return;
    const controller = new AbortController();
    void Promise.all([loadMetrics(), loadRecords(controller.signal)]);
    return () => controller.abort();
  }, [screen, loadMetrics, loadRecords]);

  function switchView(next: AdminView) {
    setView(next);
    setRecordSet({ view: next, records: [] });
    setPage(1);
    setQueryInput("");
    setQuery("");
    setStatus("all");
    setError("");
  }

  async function handleLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    setBusy(true);
    setError("");
    try {
      await api("/api/auth/sign-in/email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.get("email"),
          password: formData.get("password"),
          rememberMe: true,
        }),
      });
      const authSession = await api<Session>("/api/auth/get-session");
      setSession(authSession);
      setScreen("ready");
      form.reset();
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : l("邮箱或密码不正确。", "The email or password is incorrect."),
      );
    } finally {
      setBusy(false);
    }
  }

  async function handleSetup(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const email = String(formData.get("email") ?? "");
    const password = String(formData.get("password") ?? "");
    setBusy(true);
    setError("");
    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      const bootstrapToken = String(formData.get("token") ?? "");
      if (bootstrapToken) headers.Authorization = `Bearer ${bootstrapToken}`;
      await api("/api/admin/bootstrap", {
        method: "POST",
        headers,
        body: JSON.stringify({
          name: formData.get("name"),
          email,
          password,
          invite: formData.get("invite"),
        }),
      });
      await api("/api/auth/sign-in/email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, rememberMe: true }),
      });
      const authSession = await api<Session>("/api/auth/get-session");
      setSession(authSession);
      setScreen("ready");
      window.history.replaceState({}, "", "/admin");
      setNotice(l("管理员已创建。", "Administrator created."));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Setup failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleLogout() {
    await fetch("/api/auth/sign-out", {
      method: "POST",
      credentials: "include",
    });
    setSession(null);
    setRecordSet({ view, records: [] });
    setScreen("login");
  }

  async function refresh() {
    await Promise.all([loadMetrics(), loadRecords()]);
  }

  async function updateRecordStatus(
    type: "inquiries" | "inbound",
    id: string,
    next: RecordStatus,
  ) {
    const path =
      type === "inquiries"
        ? `/api/admin/inquiries/${id}`
        : `/api/admin/emails/inbound/${id}`;
    await api(path, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: next }),
    });
    setNotice(l("状态已更新。", "Status updated."));
    await refresh();
  }

  function requestDelete(
    type: "inquiries" | "inbound" | "outbound",
    id: string,
  ) {
    const paths = {
      inquiries: `/api/admin/inquiries/${id}`,
      inbound: `/api/admin/emails/inbound/${id}`,
      outbound: `/api/admin/emails/outbound/${id}`,
    };
    setConfirmation({
      title: l("确认删除", "Confirm deletion"),
      message: l(
        "删除后无法恢复，是否继续？",
        "This cannot be undone. Continue?",
      ),
      action: async () => {
        await api(paths[type], { method: "DELETE" });
        setInboundDetail(null);
        setOutboundDetail(null);
        setNotice(l("记录已删除。", "Record deleted."));
        await refresh();
      },
    });
  }

  async function openInbound(id: string) {
    setLoading(true);
    try {
      const detail = await api<InboundDetail>(
        `/api/admin/emails/inbound/${id}`,
      );
      setInboundDetail(detail);
      await Promise.all([loadMetrics(), loadRecords()]);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Could not open message",
      );
    } finally {
      setLoading(false);
    }
  }

  async function openOutbound(id: string) {
    setLoading(true);
    try {
      setOutboundDetail(
        await api<OutboundDetail>(`/api/admin/emails/outbound/${id}`),
      );
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Could not open message",
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleCompose(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      const response = await fetch("/api/admin/emails/send", {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      const result = (await response.json()) as {
        ok?: boolean;
        error?: string;
      };
      if (!response.ok) throw new Error(result.error || "Delivery failed");
      setCompose(null);
      setNotice(l("邮件已交付给发送服务。", "Message accepted for delivery."));
      switchView("outbound");
      await loadMetrics();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Delivery failed");
    } finally {
      setBusy(false);
    }
  }

  async function retryOutbound(id: string) {
    setBusy(true);
    try {
      await api(`/api/admin/emails/outbound/${id}/retry`, { method: "POST" });
      setNotice(l("重试成功。", "Retry succeeded."));
      setOutboundDetail(null);
      await refresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Retry failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleUserForm(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      if (userDialog?.mode === "password") {
        await api(`/api/admin/users/${userDialog.user.id}/password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ password: formData.get("password") }),
        });
        setNotice(l("密码已更新。", "Password updated."));
      } else {
        await api("/api/admin/users", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: formData.get("name"),
            email: formData.get("email"),
            password: formData.get("password"),
          }),
        });
        setNotice(l("管理员已添加。", "Administrator added."));
      }
      setUserDialog(null);
      await refresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "User update failed");
    } finally {
      setBusy(false);
    }
  }

  async function toggleBan(target: AdminUser) {
    await api(`/api/admin/users/${target.id}/toggle-ban`, { method: "POST" });
    setNotice(
      target.banned
        ? l("账号已启用。", "Account enabled.")
        : l("账号已停用。", "Account disabled."),
    );
    await refresh();
  }

  function requestUserDelete(target: AdminUser) {
    setConfirmation({
      title: l("删除管理员", "Delete administrator"),
      message: l(`确认删除 ${target.email}？`, `Delete ${target.email}?`),
      action: async () => {
        await api(`/api/admin/users/${target.id}`, { method: "DELETE" });
        setNotice(l("管理员已删除。", "Administrator deleted."));
        await refresh();
      },
    });
  }

  if (screen === "checking") {
    return (
      <section className="admin-shell admin-shell--loading" aria-busy="true">
        <PageMeta title={l("管理后台", "Admin")} />
        <p>ACCSCI CONTROL · CONNECTING</p>
      </section>
    );
  }

  if (screen === "setup" || screen === "login") {
    return (
      <section className="admin-shell admin-login">
        <PageMeta title={l("管理后台登录", "Admin login")} />
        <form
          className="admin-login__card"
          onSubmit={screen === "setup" ? handleSetup : handleLogin}
        >
          <span>
            ACCSCI CONTROL / {screen === "setup" ? "FIRST RUN" : "PRIVATE"}
          </span>
          <h1>
            {screen === "setup"
              ? l("创建首位管理员", "Create the first administrator")
              : l("进入管理后台", "Enter the control room")}
          </h1>
          <p>
            {screen === "setup"
              ? invite
                ? l(
                    "此邀请仅可用于指定邮箱创建首位管理员；账号创建后链接立即失效。",
                    "This invitation can create the first administrator only for the specified email, then it expires.",
                  )
                : l(
                    "使用部署时配置的初始化令牌，仅可创建一次。",
                    "Use the deployment bootstrap token. Setup is available once.",
                  )
              : l(
                  "管理项目咨询、邮件收发、附件与团队账号。",
                  "Manage inquiries, mail, attachments, and team accounts.",
                )}
          </p>
          {screen === "setup" && (
            <>
              {invite ? (
                <input type="hidden" name="invite" value={invite} />
              ) : (
                <Field
                  label={l("初始化令牌", "Bootstrap token")}
                  id="setup-token"
                >
                  <input
                    id="setup-token"
                    name="token"
                    type="password"
                    autoComplete="off"
                    required
                  />
                </Field>
              )}
              <Field label={l("姓名", "Name")} id="setup-name">
                <input
                  id="setup-name"
                  name="name"
                  autoComplete="name"
                  required
                />
              </Field>
            </>
          )}
          <Field
            label={l("管理员邮箱", "Administrator email")}
            id="admin-email"
          >
            <input
              id="admin-email"
              name="email"
              type="email"
              autoComplete="username"
              defaultValue={screen === "setup" ? invitedEmail : undefined}
              readOnly={screen === "setup" && Boolean(invitedEmail)}
              required
            />
          </Field>
          <Field
            label={l("密码", "Password")}
            id={screen === "setup" ? "new-password" : "current-password"}
            hint={
              screen === "setup"
                ? l("至少 12 个字符", "At least 12 characters")
                : undefined
            }
          >
            <input
              id={screen === "setup" ? "new-password" : "current-password"}
              name="password"
              type="password"
              minLength={screen === "setup" ? 12 : undefined}
              autoComplete={
                screen === "setup" ? "new-password" : "current-password"
              }
              required
            />
          </Field>
          {error && (
            <p className="admin-alert admin-alert--error" role="alert">
              {error}
            </p>
          )}
          <button className="button" type="submit" disabled={busy}>
            {busy
              ? l("处理中…", "Working…")
              : screen === "setup"
                ? l("创建并登录", "Create and sign in")
                : l("登录", "Sign in")}
          </button>
        </form>
      </section>
    );
  }

  return (
    <section className="admin-shell">
      <PageMeta title={l("管理后台", "Admin")} />
      <div className="admin-live" aria-live="polite" aria-atomic="true">
        {notice}
      </div>
      <header className="admin-header">
        <div>
          <span>ACCSCI CONTROL / LIVE</span>
          <h1>{l("运营控制台", "Operations console")}</h1>
          <p>
            {session?.user.name} · {session?.user.email}
          </p>
        </div>
        <div className="admin-header__actions">
          <button
            className="admin-button admin-button--primary"
            type="button"
            onClick={() => {
              setError("");
              setCompose({ mode: "new" });
            }}
          >
            {l("写邮件", "Compose")}
          </button>
          <button
            className="admin-button"
            type="button"
            onClick={() => void refresh()}
          >
            {l("刷新", "Refresh")}
          </button>
          <button
            className="admin-button"
            type="button"
            onClick={() => void handleLogout()}
          >
            {l("退出", "Sign out")}
          </button>
        </div>
      </header>

      <section className="admin-metrics" aria-label={l("数据概览", "Overview")}>
        <Metric
          label={l("项目咨询", "Inquiries")}
          value={metrics.contacts}
          detail={l(
            `${metrics.unreadContacts} 条待处理`,
            `${metrics.unreadContacts} new`,
          )}
        />
        <Metric
          label={l("收件箱", "Inbox")}
          value={metrics.emails}
          detail={l(
            `${metrics.unreadEmails} 封未读`,
            `${metrics.unreadEmails} unread`,
          )}
        />
        <Metric
          label={l("已发送", "Sent")}
          value={metrics.sent}
          detail={
            metrics.failed
              ? l(`${metrics.failed} 封失败`, `${metrics.failed} failed`)
              : l("投递记录", "delivery log")
          }
          danger={metrics.failed > 0}
        />
        <Metric
          label={l("管理员", "Admins")}
          value={metrics.users}
          detail={l("团队账号", "team accounts")}
        />
      </section>

      <section className="admin-console">
        <nav className="admin-nav" aria-label={l("后台功能", "Admin sections")}>
          {(
            [
              ["inquiries", l("项目咨询", "Inquiries"), metrics.contacts],
              ["inbound", l("收件箱", "Inbox"), metrics.emails],
              [
                "outbound",
                l("发件箱", "Outbox"),
                metrics.sent + metrics.failed,
              ],
              ["users", l("用户管理", "Users"), metrics.users],
            ] as const
          ).map(([id, label, count]) => (
            <button
              key={id}
              type="button"
              className={view === id ? "is-active" : ""}
              aria-current={view === id ? "page" : undefined}
              onClick={() => switchView(id)}
            >
              <span>{label}</span>
              <strong>{count}</strong>
            </button>
          ))}
        </nav>

        <div className="admin-workspace">
          <header className="admin-toolbar">
            <div>
              <p className="admin-eyebrow">{viewLabel(view)}</p>
              <h2>{viewTitle(view, l)}</h2>
            </div>
            {view === "users" ? (
              <button
                className="admin-button admin-button--primary"
                type="button"
                onClick={() => {
                  setError("");
                  setUserDialog({ mode: "create" });
                }}
              >
                {l("添加管理员", "Add administrator")}
              </button>
            ) : (
              <search>
                <form
                  className="admin-filters"
                  onSubmit={(event) => {
                    event.preventDefault();
                    setPage(1);
                    setQuery(queryInput);
                  }}
                >
                  <label className="visually-hidden" htmlFor="admin-search">
                    {l("搜索记录", "Search records")}
                  </label>
                  <input
                    id="admin-search"
                    type="search"
                    value={queryInput}
                    onChange={(event) => setQueryInput(event.target.value)}
                    placeholder={l("搜索…", "Search…")}
                  />
                  <label className="visually-hidden" htmlFor="admin-status">
                    {l("状态", "Status")}
                  </label>
                  <select
                    id="admin-status"
                    value={status}
                    onChange={(event) => {
                      setPage(1);
                      setStatus(event.target.value);
                    }}
                  >
                    <option value="all">{l("全部状态", "All statuses")}</option>
                    {view === "outbound" ? (
                      <>
                        <option value="queued">Queued</option>
                        <option value="sending">Sending</option>
                        <option value="sent">Sent</option>
                        <option value="failed">Failed</option>
                      </>
                    ) : (
                      <>
                        <option value="new">{l("新建", "New")}</option>
                        <option value="read">{l("已读", "Read")}</option>
                        <option value="archived">
                          {l("已归档", "Archived")}
                        </option>
                      </>
                    )}
                  </select>
                  <button className="admin-button" type="submit">
                    {l("搜索", "Search")}
                  </button>
                </form>
              </search>
            )}
          </header>

          {error && !compose && (
            <div className="admin-alert admin-alert--error" role="alert">
              <span>{error}</span>
              <button type="button" onClick={() => setError("")}>
                {l("关闭", "Dismiss")}
              </button>
            </div>
          )}
          {loading ? (
            <div className="admin-empty" aria-busy="true">
              {l("正在加载…", "Loading…")}
            </div>
          ) : records.length === 0 ? (
            <div className="admin-empty">
              <span>NO SIGNALS</span>
              <p>
                {l(
                  "当前筛选条件下没有记录。",
                  "No records match the current filters.",
                )}
              </p>
            </div>
          ) : (
            <RecordList
              view={view}
              records={records}
              locale={locale}
              currentUserId={session?.user.id ?? ""}
              l={l}
              onStatus={updateRecordStatus}
              onDelete={requestDelete}
              onOpenInbound={openInbound}
              onOpenOutbound={openOutbound}
              onResetPassword={(target) => {
                setError("");
                setUserDialog({ mode: "password", user: target });
              }}
              onToggleBan={toggleBan}
              onDeleteUser={requestUserDelete}
            />
          )}

          {view !== "users" && pagination.pages > 1 && (
            <nav
              className="admin-pagination"
              aria-label={l("分页", "Pagination")}
            >
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                {l("上一页", "Previous")}
              </button>
              <span>
                {page} / {pagination.pages} · {pagination.total}
              </span>
              <button
                type="button"
                disabled={page >= pagination.pages}
                onClick={() =>
                  setPage((current) => Math.min(pagination.pages, current + 1))
                }
              >
                {l("下一页", "Next")}
              </button>
            </nav>
          )}
        </div>
      </section>

      <InboundDialog
        detail={inboundDetail}
        l={l}
        locale={locale}
        onClose={() => setInboundDetail(null)}
        onCompose={(mode, source) => {
          setInboundDetail(null);
          setError("");
          setCompose({ mode, source });
        }}
        onDelete={(id) => requestDelete("inbound", id)}
      />
      <OutboundDialog
        detail={outboundDetail}
        l={l}
        locale={locale}
        busy={busy}
        onClose={() => setOutboundDetail(null)}
        onRetry={retryOutbound}
        onDelete={(id) => requestDelete("outbound", id)}
      />
      <ComposeDialog
        state={compose}
        l={l}
        busy={busy}
        error={compose ? error : ""}
        onClose={() => setCompose(null)}
        onSubmit={handleCompose}
      />
      <UserDialog
        state={userDialog}
        l={l}
        busy={busy}
        error={userDialog ? error : ""}
        onClose={() => setUserDialog(null)}
        onSubmit={handleUserForm}
      />
      <ConfirmDialog
        state={confirmation}
        l={l}
        busy={busy}
        onClose={() => setConfirmation(null)}
        onConfirm={async () => {
          if (!confirmation) return;
          setBusy(true);
          try {
            await confirmation.action();
            setConfirmation(null);
          } catch (caught) {
            setError(
              caught instanceof Error ? caught.message : "Action failed",
            );
          } finally {
            setBusy(false);
          }
        }}
      />
    </section>
  );
}

function Field({
  label,
  id,
  hint,
  children,
}: {
  label: string;
  id: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="admin-field">
      <label htmlFor={id}>{label}</label>
      {hint && <small id={`${id}-hint`}>{hint}</small>}
      {children}
    </div>
  );
}

function Metric({
  label,
  value,
  detail,
  danger = false,
}: {
  label: string;
  value: number;
  detail: string;
  danger?: boolean;
}) {
  return (
    <article className={danger ? "is-danger" : ""}>
      <span>{label}</span>
      <strong>{value.toString().padStart(2, "0")}</strong>
      <small>{detail}</small>
    </article>
  );
}

function RecordList({
  view,
  records,
  locale,
  currentUserId,
  l,
  onStatus,
  onDelete,
  onOpenInbound,
  onOpenOutbound,
  onResetPassword,
  onToggleBan,
  onDeleteUser,
}: {
  view: AdminView;
  records: RecordItem[];
  locale: "zh" | "en";
  currentUserId: string;
  l: (zh: string, en: string) => string;
  onStatus: (
    type: "inquiries" | "inbound",
    id: string,
    status: RecordStatus,
  ) => Promise<void>;
  onDelete: (type: "inquiries" | "inbound" | "outbound", id: string) => void;
  onOpenInbound: (id: string) => Promise<void>;
  onOpenOutbound: (id: string) => Promise<void>;
  onResetPassword: (user: AdminUser) => void;
  onToggleBan: (user: AdminUser) => Promise<void>;
  onDeleteUser: (user: AdminUser) => void;
}) {
  return (
    <ul className="admin-records">
      {records.map((item) => {
        if (view === "inquiries") {
          const record = item as InquiryRecord;
          return (
            <li className="admin-record" key={record.id}>
              <RecordMeta
                date={record.createdAt}
                status={record.status}
                locale={locale}
              />
              <h3>{record.name}</h3>
              <p className="admin-record__identity">
                <a href={`mailto:${record.email}`}>{record.email}</a>
                {record.organization && <> · {record.organization}</>}
              </p>
              <div className="admin-record__tags">
                {record.topics.map((topic) => (
                  <span key={topic}>{topic}</span>
                ))}
              </div>
              <p>{record.message}</p>
              <RecordActions>
                <StatusSelect
                  value={record.status}
                  l={l}
                  onChange={(next) =>
                    void onStatus("inquiries", record.id, next)
                  }
                />
                <button
                  type="button"
                  className="is-danger"
                  onClick={() => onDelete("inquiries", record.id)}
                >
                  {l("删除", "Delete")}
                </button>
              </RecordActions>
            </li>
          );
        }
        if (view === "inbound") {
          const record = item as InboundRecord;
          return (
            <li
              className={`admin-record ${record.status === "new" ? "is-unread" : ""}`}
              key={record.id}
            >
              <RecordMeta
                date={record.createdAt}
                status={record.status}
                locale={locale}
                extra={formatBytes(record.size)}
              />
              <button
                className="admin-record__title"
                type="button"
                onClick={() => void onOpenInbound(record.id)}
              >
                <h3>{record.subject}</h3>
              </button>
              <p className="admin-record__identity">
                {record.from} → {record.to}
              </p>
              {record.bodyPreview && (
                <p className="admin-record__preview">{record.bodyPreview}</p>
              )}
              <RecordActions>
                <button
                  type="button"
                  onClick={() => void onOpenInbound(record.id)}
                >
                  {l("阅读", "Open")}
                </button>
                <StatusSelect
                  value={record.status}
                  l={l}
                  onChange={(next) => void onStatus("inbound", record.id, next)}
                />
                <button
                  type="button"
                  className="is-danger"
                  onClick={() => onDelete("inbound", record.id)}
                >
                  {l("删除", "Delete")}
                </button>
              </RecordActions>
            </li>
          );
        }
        if (view === "outbound") {
          const record = item as OutboundRecord;
          return (
            <li className="admin-record" key={record.id}>
              <RecordMeta
                date={record.sentAt ?? record.createdAt}
                status={record.status}
                locale={locale}
                extra={`${record.kind} · #${record.attemptCount}`}
              />
              <button
                className="admin-record__title"
                type="button"
                onClick={() => void onOpenOutbound(record.id)}
              >
                <h3>{record.subject}</h3>
              </button>
              <p className="admin-record__identity">
                → {formatRecipients(record.to) || "—"}
              </p>
              {record.lastError && (
                <p className="admin-record__error">{record.lastError}</p>
              )}
              <RecordActions>
                <button
                  type="button"
                  onClick={() => void onOpenOutbound(record.id)}
                >
                  {l("投递详情", "Delivery details")}
                </button>
                <button
                  type="button"
                  className="is-danger"
                  onClick={() => onDelete("outbound", record.id)}
                >
                  {l("删除", "Delete")}
                </button>
              </RecordActions>
            </li>
          );
        }
        const target = item as AdminUser;
        return (
          <li className="admin-record admin-user" key={target.id}>
            <RecordMeta
              date={target.createdAt}
              status={target.banned ? "disabled" : target.role}
              locale={locale}
            />
            <h3>{target.name}</h3>
            <p className="admin-record__identity">
              {target.email}
              {target.id === currentUserId && (
                <> · {l("当前账号", "Current account")}</>
              )}
            </p>
            <RecordActions>
              <button type="button" onClick={() => onResetPassword(target)}>
                {l("重置密码", "Reset password")}
              </button>
              <button
                type="button"
                disabled={target.id === currentUserId}
                onClick={() => void onToggleBan(target)}
              >
                {target.banned ? l("启用", "Enable") : l("停用", "Disable")}
              </button>
              <button
                type="button"
                className="is-danger"
                disabled={target.id === currentUserId}
                onClick={() => onDeleteUser(target)}
              >
                {l("删除", "Delete")}
              </button>
            </RecordActions>
          </li>
        );
      })}
    </ul>
  );
}

function RecordMeta({
  date,
  status,
  locale,
  extra,
}: {
  date: string;
  status: string;
  locale: "zh" | "en";
  extra?: string;
}) {
  return (
    <div className="admin-record__meta">
      <time dateTime={date}>{formatDate(date, locale)}</time>
      <span>
        {extra && `${extra} · `}
        {status}
      </span>
    </div>
  );
}

function RecordActions({ children }: { children: ReactNode }) {
  return <div className="admin-record__actions">{children}</div>;
}

function StatusSelect({
  value,
  l,
  onChange,
}: {
  value: RecordStatus;
  l: (zh: string, en: string) => string;
  onChange: (value: RecordStatus) => void;
}) {
  return (
    <select
      aria-label={l("更新状态", "Update status")}
      value={value}
      onChange={(event) => onChange(event.target.value as RecordStatus)}
    >
      <option value="new">{l("新建", "New")}</option>
      <option value="read">{l("已读", "Read")}</option>
      <option value="archived">{l("已归档", "Archived")}</option>
    </select>
  );
}

function InboundDialog({
  detail,
  l,
  locale,
  onClose,
  onCompose,
  onDelete,
}: {
  detail: InboundDetail | null;
  l: (zh: string, en: string) => string;
  locale: "zh" | "en";
  onClose: () => void;
  onCompose: (mode: "reply" | "forward", source: InboundDetail) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <ModalDialog
      open={Boolean(detail)}
      title={detail?.subject ?? ""}
      onClose={onClose}
      wide
    >
      {detail && (
        <>
          <div className="admin-message-meta">
            <span>{detail.from}</span>
            <span>→ {detail.to}</span>
            <time dateTime={detail.createdAt}>
              {formatDate(detail.createdAt, locale)}
            </time>
          </div>
          <pre className="admin-message-body">
            {detail.text ||
              l(
                "此邮件没有纯文本正文。",
                "This message has no plain-text body.",
              )}
          </pre>
          {detail.attachments.length > 0 && (
            <section className="admin-attachments">
              <h3>{l("附件", "Attachments")}</h3>
              <ul>
                {detail.attachments.map((attachment) => (
                  <li key={attachment.index}>
                    <a
                      href={`/api/admin/emails/inbound/${detail.id}/attachments/${attachment.index}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {attachment.filename}
                    </a>
                    <small>
                      {attachment.contentType} · {formatBytes(attachment.size)}
                    </small>
                    {attachment.previewable &&
                      attachment.contentType.startsWith("image/") && (
                        <img
                          src={`/api/admin/emails/inbound/${detail.id}/attachments/${attachment.index}`}
                          alt={attachment.filename}
                        />
                      )}
                  </li>
                ))}
              </ul>
            </section>
          )}
          <div className="admin-dialog__actions">
            <button
              className="admin-button admin-button--primary"
              type="button"
              onClick={() => onCompose("reply", detail)}
            >
              {l("回复", "Reply")}
            </button>
            <button
              className="admin-button"
              type="button"
              onClick={() => onCompose("forward", detail)}
            >
              {l("转发", "Forward")}
            </button>
            <a
              className="admin-button"
              href={`/api/admin/emails/inbound/${detail.id}/raw`}
            >
              {l("下载原始邮件", "Download .eml")}
            </a>
            <button
              className="admin-button is-danger"
              type="button"
              onClick={() => onDelete(detail.id)}
            >
              {l("删除", "Delete")}
            </button>
          </div>
        </>
      )}
    </ModalDialog>
  );
}

function OutboundDialog({
  detail,
  l,
  locale,
  busy,
  onClose,
  onRetry,
  onDelete,
}: {
  detail: OutboundDetail | null;
  l: (zh: string, en: string) => string;
  locale: "zh" | "en";
  busy: boolean;
  onClose: () => void;
  onRetry: (id: string) => Promise<void>;
  onDelete: (id: string) => void;
}) {
  return (
    <ModalDialog
      open={Boolean(detail)}
      title={detail?.subject ?? ""}
      onClose={onClose}
      wide
    >
      {detail && (
        <>
          <div className="admin-message-meta">
            <span>→ {detail.to.join(", ")}</span>
            <span>{detail.status}</span>
            <time dateTime={detail.createdAt}>
              {formatDate(detail.createdAt, locale)}
            </time>
          </div>
          <pre className="admin-message-body">{detail.textBody}</pre>
          {detail.attachments.length > 0 && (
            <section className="admin-attachments">
              <h3>{l("附件", "Attachments")}</h3>
              <ul>
                {detail.attachments.map((attachment) => (
                  <li key={attachment.id}>
                    <span>{attachment.filename}</span>
                    <small>
                      {attachment.contentType} · {formatBytes(attachment.size)}
                    </small>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <section className="admin-timeline">
            <h3>{l("投递日志", "Delivery log")}</h3>
            <ol>
              {detail.events.map((event) => (
                <li key={event.id}>
                  <span>{event.status}</span>
                  <p>{event.message}</p>
                  <time dateTime={event.createdAt}>
                    {formatDate(event.createdAt, locale)}
                  </time>
                </li>
              ))}
            </ol>
          </section>
          <div className="admin-dialog__actions">
            {detail.status === "failed" && (
              <button
                className="admin-button admin-button--primary"
                type="button"
                disabled={busy}
                onClick={() => void onRetry(detail.id)}
              >
                {l("重试发送", "Retry delivery")}
              </button>
            )}
            <button
              className="admin-button is-danger"
              type="button"
              onClick={() => onDelete(detail.id)}
            >
              {l("删除记录", "Delete record")}
            </button>
          </div>
        </>
      )}
    </ModalDialog>
  );
}

function ComposeDialog({
  state,
  l,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  state: ComposeState | null;
  l: (zh: string, en: string) => string;
  busy: boolean;
  error: string;
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
}) {
  const source = state?.source;
  const subject =
    state?.mode === "reply"
      ? `Re: ${source?.subject ?? ""}`
      : state?.mode === "forward"
        ? `Fwd: ${source?.subject ?? ""}`
        : "";
  const to =
    state?.mode === "reply" ? (source?.replyTo ?? source?.from ?? "") : "";
  return (
    <ModalDialog
      open={Boolean(state)}
      title={
        state?.mode === "reply"
          ? l("回复邮件", "Reply")
          : state?.mode === "forward"
            ? l("转发邮件", "Forward")
            : l("写邮件", "Compose message")
      }
      onClose={onClose}
      wide
    >
      {state && (
        <form className="admin-form" onSubmit={(event) => void onSubmit(event)}>
          <input type="hidden" name="mode" value={state.mode} />
          <input type="hidden" name="sourceId" value={source?.id ?? ""} />
          <Field
            label={l("收件人", "To")}
            id="compose-to"
            hint={l(
              "多个地址用逗号分隔",
              "Separate multiple addresses with commas",
            )}
          >
            <input
              id="compose-to"
              name="to"
              type="text"
              inputMode="email"
              defaultValue={to}
              required
            />
          </Field>
          <div className="admin-form__row">
            <Field label="CC" id="compose-cc">
              <input id="compose-cc" name="cc" type="text" inputMode="email" />
            </Field>
            <Field label="BCC" id="compose-bcc">
              <input
                id="compose-bcc"
                name="bcc"
                type="text"
                inputMode="email"
              />
            </Field>
          </div>
          <Field label={l("主题", "Subject")} id="compose-subject">
            <input
              id="compose-subject"
              name="subject"
              defaultValue={subject}
              maxLength={240}
              required
            />
          </Field>
          <Field label={l("正文", "Message")} id="compose-body">
            <textarea
              id="compose-body"
              name="body"
              rows={12}
              maxLength={100000}
              required
            />
          </Field>
          {state.mode !== "new" && (
            <label className="admin-check">
              <input
                type="checkbox"
                name="includeOriginal"
                value="true"
                defaultChecked
              />
              {l("包含原邮件内容", "Include original message")}
            </label>
          )}
          {state.mode === "forward" && source?.hasAttachments && (
            <label className="admin-check">
              <input
                type="checkbox"
                name="includeOriginalAttachments"
                value="true"
                defaultChecked
              />
              {l("包含原邮件附件", "Include original attachments")}
            </label>
          )}
          <Field
            label={l("添加附件", "Add attachments")}
            id="compose-attachments"
            hint={l(
              "最多 8 个，单个 8 MB，总计 12 MB",
              "Up to 8 files, 8 MB each, 12 MB total",
            )}
          >
            <input
              id="compose-attachments"
              name="attachments"
              type="file"
              multiple
            />
          </Field>
          {error && (
            <p className="admin-alert admin-alert--error" role="alert">
              {error}
            </p>
          )}
          <div className="admin-dialog__actions">
            <button
              className="admin-button admin-button--primary"
              type="submit"
              disabled={busy}
            >
              {busy ? l("发送中…", "Sending…") : l("发送邮件", "Send message")}
            </button>
            <button className="admin-button" type="button" onClick={onClose}>
              {l("取消", "Cancel")}
            </button>
          </div>
        </form>
      )}
    </ModalDialog>
  );
}

function UserDialog({
  state,
  l,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  state: { mode: "create" } | { mode: "password"; user: AdminUser } | null;
  l: (zh: string, en: string) => string;
  busy: boolean;
  error: string;
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
}) {
  return (
    <ModalDialog
      open={Boolean(state)}
      title={
        state?.mode === "password"
          ? l("重置密码", "Reset password")
          : l("添加管理员", "Add administrator")
      }
      onClose={onClose}
    >
      {state && (
        <form className="admin-form" onSubmit={(event) => void onSubmit(event)}>
          {state.mode === "create" && (
            <>
              <Field label={l("姓名", "Name")} id="user-name">
                <input id="user-name" name="name" autoComplete="off" required />
              </Field>
              <Field label={l("邮箱", "Email")} id="user-email">
                <input
                  id="user-email"
                  name="email"
                  type="email"
                  autoComplete="off"
                  required
                />
              </Field>
            </>
          )}
          {state.mode === "password" && (
            <p>
              {state.user.name} · {state.user.email}
            </p>
          )}
          <Field
            label={l("新密码", "New password")}
            id="user-password"
            hint={l("至少 12 个字符", "At least 12 characters")}
          >
            <input
              id="user-password"
              name="password"
              type="password"
              minLength={12}
              autoComplete="new-password"
              required
            />
          </Field>
          {error && (
            <p className="admin-alert admin-alert--error" role="alert">
              {error}
            </p>
          )}
          <div className="admin-dialog__actions">
            <button
              className="admin-button admin-button--primary"
              type="submit"
              disabled={busy}
            >
              {l("保存", "Save")}
            </button>
            <button className="admin-button" type="button" onClick={onClose}>
              {l("取消", "Cancel")}
            </button>
          </div>
        </form>
      )}
    </ModalDialog>
  );
}

function ConfirmDialog({
  state,
  l,
  busy,
  onClose,
  onConfirm,
}: {
  state: { title: string; message: string } | null;
  l: (zh: string, en: string) => string;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}) {
  return (
    <ModalDialog
      open={Boolean(state)}
      title={state?.title ?? ""}
      onClose={onClose}
    >
      {state && (
        <>
          <p>{state.message}</p>
          <div className="admin-dialog__actions">
            <button
              className="admin-button is-danger"
              type="button"
              disabled={busy}
              onClick={() => void onConfirm()}
            >
              {l("确认删除", "Confirm")}
            </button>
            <button className="admin-button" type="button" onClick={onClose}>
              {l("取消", "Cancel")}
            </button>
          </div>
        </>
      )}
    </ModalDialog>
  );
}

function ModalDialog({
  open,
  title,
  onClose,
  wide = false,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  wide?: boolean;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useRef(`dialog-${crypto.randomUUID()}`);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      className={`admin-dialog ${wide ? "admin-dialog--wide" : ""}`}
      aria-labelledby={titleId.current}
      onClose={onClose}
      onCancel={onClose}
    >
      <header>
        <h2 id={titleId.current}>{title}</h2>
        <button type="button" aria-label="Close" onClick={onClose}>
          ×
        </button>
      </header>
      <div className="admin-dialog__content">{children}</div>
    </dialog>
  );
}

function viewLabel(view: AdminView) {
  return view === "inquiries"
    ? "PROJECT BRIEFS"
    : view === "inbound"
      ? "INBOUND MAIL"
      : view === "outbound"
        ? "DELIVERY LOG"
        : "ACCESS CONTROL";
}

function viewTitle(view: AdminView, l: (zh: string, en: string) => string) {
  return view === "inquiries"
    ? l("项目咨询", "Project inquiries")
    : view === "inbound"
      ? l("邮件收件箱", "Mail inbox")
      : view === "outbound"
        ? l("发件与投递", "Outbox and delivery")
        : l("管理员账号", "Administrator accounts");
}

function formatDate(value: string, locale: "zh" | "en") {
  return new Intl.DateTimeFormat(locale === "zh" ? "zh-CN" : "en", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}
